import { zodResolver } from '@hookform/resolvers/zod';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { api, ApiError, unwrap, type Schemas } from '../api/client';
import { invitationErrorText, readInviteToken } from '../features/shop/lib';
import { PasswordField } from '../features/shop/ui';
import { AuthLayout } from './AuthLayout';
import { authButtonSx } from './styles';
import { useAuth } from './AuthProvider';

const schema = z
  .object({
    name: z.string().trim().min(2, 'Enter your name'),
    password: z.string().min(8, 'At least 8 characters').max(128, 'At most 128 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'The passwords do not match',
    path: ['confirm'],
  });
type Values = z.infer<typeof schema>;

/** Public page behind the staff invitation link (`/accept-invitation#token=…`). */
export function AcceptInvitationPage() {
  const location = useLocation();
  const [token] = useState(() => readInviteToken(location.search, location.hash));
  const info = useQuery({
    queryKey: ['invitation-info', token],
    queryFn: () => unwrap(api.POST('/auth/invitation-info', { body: { token: token ?? '' } })),
    enabled: token !== null,
    retry: false,
  });

  if (!token || info.isError) {
    const code = info.error instanceof ApiError ? info.error.code : 'INVITATION_INVALID';
    const network = info.error && !(info.error instanceof ApiError && info.error.status < 500);
    const text = network
      ? {
          title: 'Could not reach the server',
          body: 'Check your connection and open the link again.',
        }
      : invitationErrorText(code);
    return (
      <AuthLayout title={text.title} subtitle={text.body}>
        <Button component={RouterLink} to="/login" variant="contained" fullWidth sx={authButtonSx}>
          Go to sign in
        </Button>
      </AuthLayout>
    );
  }

  if (info.isPending)
    return (
      <AuthLayout title="Opening your invitation" subtitle="One moment…">
        <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      </AuthLayout>
    );

  return <AcceptForm token={token} info={info.data} />;
}

function AcceptForm({ token, info }: { token: string; info: Schemas['InvitationInfo'] }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: info.name, password: '', confirm: '' },
  });

  const password = useWatch({ control, name: 'password' });

  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    let account: Schemas['AcceptInvitationResult'];
    try {
      account = await unwrap(
        api.POST('/auth/accept-invitation', {
          body: { token, password: v.password, name: v.name },
        }),
      );
    } catch (e) {
      setError(
        !(e instanceof ApiError)
          ? 'Could not reach the server. Please try again.'
          : e.code.startsWith('INVITATION_') && e.code !== 'INVITATION_ROLES_MISSING'
            ? invitationErrorText(e.code).title
            : e.status === 409
              ? 'An account with this email or phone already exists in this shop. Sign in instead.'
              : e.message,
      );
      return;
    }
    try {
      localStorage.setItem('sm.lastShop', account.shopSlug);
    } catch {
      /* ignore */
    }
    try {
      await login({ shopSlug: account.shopSlug, login: account.login, password: v.password });
      void navigate('/', { replace: true });
    } catch {
      // The account exists; signing in by hand still works.
      void navigate('/login', { replace: true });
    }
  });

  const contact = info.email ?? info.phone;
  return (
    <AuthLayout
      title={`Join ${info.shop.name}`}
      subtitle="You were invited to work in this shop. Choose a password to activate your account."
    >
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'action.hover' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Shop ID <b>{info.shop.slug}</b>
              {contact && (
                <>
                  {' · '}sign in as <b>{contact}</b>
                </>
              )}
            </Typography>
          </Box>
          <TextField
            label="Your name"
            autoComplete="name"
            {...register('name')}
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
          />
          <PasswordField
            label="Password"
            autoComplete="new-password"
            autoFocus
            {...register('password')}
            strength={password}
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            fullWidth
          />
          <PasswordField
            label="Repeat password"
            autoComplete="new-password"
            {...register('confirm')}
            error={Boolean(errors.confirm)}
            helperText={errors.confirm?.message}
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            loading={isSubmitting}
            fullWidth
            endIcon={<ArrowForwardRounded />}
            sx={authButtonSx}
          >
            Activate my account
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
